import React, { useState, useMemo } from "react";
import { computeInterest } from "../data/mock";
import { inr } from "../lib/format";
import { Calculator, TrendingUp } from "lucide-react";

export const InterestCalculator = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState("250000");
  const [acceptanceDate, setAcceptanceDate] = useState("2025-01-15");
  const [creditPeriod, setCreditPeriod] = useState(45);

  const calc = useMemo(() => {
    const principal = Number(amount);
    if (!principal || principal <= 0) return null;
    return computeInterest(
      { principal, acceptanceDate, creditPeriod: Number(creditPeriod) },
      today
    );
  }, [amount, acceptanceDate, creditPeriod, today]);

  return (
    <section id="calculator" className="border-b border-border bg-white">
      <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal">
            Free estimator
          </span>
          <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-ink">
            What is your buyer really costing you?
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Punch in one overdue invoice and see the statutory interest accruing under Section
            16 — computed at 3× the dated RBI Bank Rate with monthly rests, as of today.
          </p>

          <div className="mt-8 space-y-5">
            <label className="block">
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                Invoice / principal amount (₹)
              </span>
              <input
                data-testid="calc-amount"
                type="number"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="baaki-input font-mono"
                placeholder="250000"
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  Date buyer accepted goods
                </span>
                <input
                  data-testid="calc-date"
                  type="date"
                  max={today}
                  value={acceptanceDate}
                  onChange={(e) => setAcceptanceDate(e.target.value)}
                  className="baaki-input font-mono"
                />
              </label>
              <label className="block">
                <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  Credit period (days, max 45)
                </span>
                <input
                  data-testid="calc-credit"
                  type="number"
                  min="0"
                  max="45"
                  value={creditPeriod}
                  onChange={(e) => setCreditPeriod(Math.min(45, Number(e.target.value)))}
                  className="baaki-input font-mono"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-paper border border-border p-6" data-testid="calc-result">
            <div className="flex items-center gap-2 border-b border-border pb-3">
              <Calculator className="h-4 w-4 text-signal" strokeWidth={1.5} />
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Live estimate · as of today
              </span>
            </div>

            {!calc ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Enter a valid amount to see your estimate.
              </p>
            ) : (
              <>
                <div className="grid sm:grid-cols-3 gap-px bg-border border border-border mt-4">
                  <Cell label="Days overdue" value={String(calc.daysOverdue)} />
                  <Cell label="Interest accrued" value={inr(calc.totalInterest)} accent />
                  <Cell label="Total recoverable" value={inr(calc.grandTotal)} />
                </div>

                {calc.daysOverdue === 0 ? (
                  <div className="mt-4 bg-success-bg border border-success/30 px-4 py-3 text-sm text-success">
                    Not yet overdue — statutory interest starts accruing after the 45-day
                    appointed day.
                  </div>
                ) : (
                  <div className="mt-4 flex items-start gap-2.5 bg-white border border-border px-4 py-3">
                    <TrendingUp className="h-4 w-4 text-signal mt-0.5 shrink-0" strokeWidth={1.5} />
                    <p className="text-[13px] leading-relaxed text-ink">
                      Interest is compounding across{" "}
                      <span className="font-mono">{calc.rows.length}</span> monthly rests. Every
                      month unpaid adds roughly{" "}
                      <span className="font-mono text-signal">
                        {inr(Math.round(calc.totalInterest / Math.max(1, calc.rows.length)))}
                      </span>{" "}
                      more.
                    </p>
                  </div>
                )}

                <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
                  Estimate only, using a curated dated RBI Bank Rate table. Not legal advice —
                  verify with counsel before serving any notice.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Cell = ({ label, value, accent }) => (
  <div className="bg-white px-4 py-4">
    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
    <div className={`mt-1 font-mono text-2xl font-semibold ${accent ? "text-signal" : "text-ink"}`}>
      {value}
    </div>
  </div>
);
